/**
 * Every time this quotation was actually emailed to the customer.
 *
 * `Email Queue` rather than anything of ours, and rather than `Version`: measured 2026-08-09,
 * `send_quotation` sets `status`/`sent_at` OUTSIDE Frappe's versioning, so the audit trail has no
 * record of a send at all — `Version` only ever shows `Quoted -> Draft`, a revision reopening the
 * document. `Email Queue` has a row per outgoing mail, with the moment it went and whether it
 * left, which is the only evidence a customer received anything.
 *
 * `Quotation.sent_at` is no substitute: it holds the LAST send, so a quotation issued three times
 * looks like it was issued once.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { str } from './wire'
import { qk } from './keys'
import type { SendRecord } from '../quotationHistory'

interface WireEmailQueue {
  name: string
  creation: string
  status: string
}

/** A quotation revised past this many rounds is a different conversation. */
const SENDS_LIMIT = 50

export function useQuotationSends(quotationName: string | null | undefined) {
  return useQuery({
    queryKey: qk.quotationSends(quotationName ?? ''),
    queryFn: async (): Promise<SendRecord[]> => {
      const page = await getResource<WireEmailQueue>('Email Queue', {
        filters: [
          ['reference_doctype', '=', 'Quotation'],
          ['reference_name', '=', quotationName ?? ''],
        ],
        fields: ['name', 'creation', 'status'],
        orderBy: 'creation desc',
        limit: SENDS_LIMIT,
      })
      return page.data.map((row) => ({ id: str(row.name), at: str(row.creation), status: str(row.status) }))
    },
    enabled: !!quotationName,
  })
}
